// shareSheetPanel.ts — screen 12 (share-sheet). The in-app "CHALLENGE A FRIEND" brag surface: a
// bottom sheet whose preview shows the ACTUAL share artifact (the 9:16 portrait route card that gets
// exported), then SHARE / COPY LINK / SAVE IMAGE actions. Renderer-only; the actual share/copy/save
// effects are passed in by the caller (ResultScene) so this stays decoupled from share.ts wiring.
import Phaser from 'phaser';
import { PALETTE, FONT, hex } from './keys';
import { scaledFont } from './settings';
import { sheet, type Modal } from './ui';
import { ctaButton, secondaryButton, labelText } from './ui/kit';
import { icon } from './icons';
import { drawPlayerMuncher } from './muncher';
import { renderRouteCardPortrait, type ShareCardData } from './share';

export interface ShareSheetData {
  topic?: string;
  topicSlug?: string;
  score?: number;
  url?: string;
  onShare?: () => void;
  onCopy?: () => void;
  onSave?: () => void;
  /** STRETCH #16 B2: record + share the last-3s killcam as a WebM clip. Wired only for eligible results. */
  onShareVideo?: () => void;
  /** true iff the killcam WebM export is available (a killcam model exists AND the platform can capture a
   *  canvas to webm). Drives whether the "Share as Video" button appears at all — absent = still-only, no
   *  broken affordance. */
  canShareVideo?: boolean;
  /** FACE & FURY S5: badge the in-app brag-card preview when the run was a PERFECT GAUNTLET. */
  perfectGauntlet?: boolean;
  /** 9+ punch #26: the REAL share-card data. When present the preview renders the actual shipped 9:16
   *  portrait ROUTE card (share.ts renderRouteCardPortrait) scaled down — so the sheet finally matches
   *  what gets exported (gold frame + route grid), instead of the old cyan / no-grid mock that
   *  misrepresented the artifact on 100% of opens. Absent (e.g. the dev-bridge demo) → the mock. */
  card?: ShareCardData;
}

export function openShareSheet(scene: Phaser.Scene, data: ShareSheetData = {}): Modal {
  // #26 REAL PREVIEW: when the caller hands us the actual card data, render the shipped portrait route
  // card into the preview so the sheet never again misrepresents the export. No card → the mock
  // fallback (the dev bridge 'share-sheet' opens with no data purely to inspect the sheet chrome).
  if (data.card) return openRealPreviewSheet(scene, data, data.card);
  return openMockSheet(scene, data);
}

/**
 * S9 FIX-D (ts150 hardening): shrink a secondary button's icon+label group to fit INSIDE its plate with
 * a guaranteed horizontal padding. At 150% text scale the half-width COPY LINK / SAVE IMAGE labels ran
 * flush into (and past) their button borders — the kit centres the group but never fits it. Scales the
 * label/icon children (and their offsets) about the button centre; the plate + ≥44px hit target are
 * untouched, so the tap affordance never shrinks. Same shrink-to-fit backstop discipline as the Result's
 * NEXT UNLOCK row. No-op when the group already fits (100% scale is byte-identical).
 */
function fitButtonLabel(btn: Phaser.GameObjects.Container, btnW: number, minPadPx = 22): void {
  const kids = btn.list;
  const txt = kids.find((o): o is Phaser.GameObjects.Text => o instanceof Phaser.GameObjects.Text);
  if (!txt) return;
  // The kit lays the (icon+gap+)label group centred: the text is the rightmost element, so the group's
  // half-width is exactly the text's right edge.
  const groupW = (txt.x + txt.width / 2) * 2;
  const maxW = btnW - minPadPx;
  if (groupW <= maxW || groupW <= 0) return;
  const s = maxW / groupW;
  for (let i = 1; i < kids.length; i += 1) {
    const o = kids[i];
    if (o instanceof Phaser.GameObjects.Rectangle) continue; // the invisible hit target stays full-size
    const part = o as Phaser.GameObjects.GameObject & { x: number; setScale(n: number): unknown; setX(n: number): unknown };
    part.setScale(s);
    part.setX(part.x * s);
  }
}

/**
 * #26: the honest preview. Renders the real 9:16 portrait route card (the exact artifact shareResult
 * ships) to an offscreen canvas, uploads it as a canvas texture, and scales it into the preview slot.
 * The 1080×1920 texture is freed on sheet teardown so repeated opens never leak GPU memory.
 */
function openRealPreviewSheet(scene: Phaser.Scene, data: ShareSheetData, cardData: ShareCardData): Modal {
  const PREVIEW_H = 360;
  const PREVIEW_W = Math.round(PREVIEW_H * (1080 / 1920)); // keep the true 9:16 aspect
  const previewTopPad = 66; // grab-handle + title + a "PREVIEW" eyebrow
  const gapBelow = 24;
  const shareH = 56;
  const rowGap = 12;
  const rowH = 46;
  // STRETCH #16 B2: an optional "Share as Video" row sits between the still-share CTA and Copy/Save when
  // the killcam WebM export is available — grow the sheet to hug it (content-hug invariant preserved).
  const hasVideo = !!(data.canShareVideo && data.onShareVideo);
  const videoH = hasVideo ? 46 : 0;
  const videoGap = hasVideo ? 12 : 0;
  const contentHeight = previewTopPad + PREVIEW_H + gapBelow + shareH + videoGap + videoH + rowGap + rowH + 58;

  const m = sheet(scene, { title: 'Challenge a friend', contentHeight });
  const { card, cardW, cardH } = m;
  const top = -cardH / 2;

  // Eyebrow so the framed image reads as "this is exactly what you'll post".
  card.add(labelText(scene, 0, top + 52, 'PREVIEW · YOUR ROUTE CARD', { size: 10, color: PALETTE.scan, origin: 0.5 }));

  // Render the real portrait card once and mount it as a scaled canvas texture.
  const previewCy = top + previewTopPad + PREVIEW_H / 2;
  const canvas = renderRouteCardPortrait(cardData);
  const texKey = `sharePreview-${Math.floor(scene.time.now)}-${Math.floor(Math.random() * 1e6)}`;
  if (scene.textures.exists(texKey)) scene.textures.remove(texKey);
  scene.textures.addCanvas(texKey, canvas);
  const preview = scene.add.image(0, previewCy, texKey).setDisplaySize(PREVIEW_W, PREVIEW_H);
  // A thin frame hugging the scaled card so it doesn't float on the sheet surface.
  const frame = scene.add.graphics();
  frame.lineStyle(1.5, PALETTE.lineStrong, 1);
  frame.strokeRoundedRect(-PREVIEW_W / 2 - 2, previewCy - PREVIEW_H / 2 - 2, PREVIEW_W + 4, PREVIEW_H + 4, 12);
  card.add([preview, frame]);
  // Free the 1080×1920 canvas texture when the sheet tears down — never leak one per open.
  m.layer.once(Phaser.GameObjects.Events.DESTROY, () => {
    if (scene.textures.exists(texKey)) scene.textures.remove(texKey);
  });

  // ── actions ──
  const cw = cardW - 44;
  const shareY = previewCy + PREVIEW_H / 2 + gapBelow + shareH / 2;
  card.add(
    ctaButton(scene, 0, shareY, 'Share Result', () => (data.onShare ? data.onShare() : m.close()), {
      icon: 'share',
      width: cw,
      height: shareH,
    }),
  );
  let afterActionsY = shareY + shareH / 2;
  if (hasVideo) {
    const vy = afterActionsY + videoGap + videoH / 2;
    card.add(
      secondaryButton(scene, 0, vy, 'Share as Video', () => data.onShareVideo?.(), {
        icon: 'play',
        width: cw,
        height: videoH,
        textColor: PALETTE.scan,
      }),
    );
    afterActionsY = vy + videoH / 2;
  }
  const rowY = afterActionsY + rowGap + rowH / 2;
  const halfW = (cw - 12) / 2;
  const copyBtn = secondaryButton(scene, -halfW / 2 - 6, rowY, 'Copy Link', () => data.onCopy?.(), { icon: 'copy', width: halfW, height: rowH });
  const saveBtn = secondaryButton(scene, halfW / 2 + 6, rowY, 'Save Image', () => data.onSave?.(), { icon: 'image', width: halfW, height: rowH });
  // ts150: the scaled labels overflow the half-width plates — shrink-to-fit with guaranteed padding.
  fitButtonLabel(copyBtn, halfW);
  fitButtonLabel(saveBtn, halfW);
  card.add(copyBtn);
  card.add(saveBtn);

  return m;
}

/** Fallback preview (no card data): the lightweight drawn brag mock. Used only by the dev bridge demo. */
function openMockSheet(scene: Phaser.Scene, data: ShareSheetData): Modal {
  const topic = data.topic ?? 'Planets';
  const slug = data.topicSlug ?? topic.toLowerCase().replace(/[^a-z0-9]+/g, '');
  const score = data.score ?? 12480;
  const url = data.url ?? `triviamunchers.app/t/${slug}`;

  // Hug to content with a real bottom cushion so the Copy/Save row clears the physical sheet edge.
  const m = sheet(scene, { title: 'Challenge a friend', contentHeight: 506 });
  const { card, cardW, cardH } = m;
  const top = -cardH / 2;

  // ── brag card preview ──
  const cw = cardW - 44;
  const ch = 232;
  const cy = top + 64 + ch / 2;
  const bg = scene.add.graphics();
  bg.fillStyle(PALETTE.bg, 1);
  bg.fillRoundedRect(-cw / 2, cy - ch / 2, cw, ch, 16);
  bg.fillStyle(PALETTE.player, 0.05);
  bg.fillRoundedRect(-cw / 2, cy - ch / 2, cw, ch, 16);
  bg.lineStyle(1.4, PALETTE.lineStrong, 1);
  bg.strokeRoundedRect(-cw / 2, cy - ch / 2, cw, ch, 16);
  card.add(bg);

  // brand row (muncher + wordmark)
  const muncherG = scene.add.graphics({ x: -36, y: cy - ch / 2 + 34 });
  drawPlayerMuncher(muncherG, 26, { glow: false });
  card.add(muncherG);
  card.add(
    scene.add
      .text(-18, cy - ch / 2 + 34, 'TRIVIA MUNCHERS', {
        fontFamily: FONT.display,
        fontSize: `${scaledFont(13, 11)}px`,
        color: hex(PALETTE.text),
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5),
  );

  card.add(labelText(scene, 0, cy - ch / 2 + 64, `TOPIC · ${topic}`, { size: 10, color: PALETTE.scan, origin: 0.5 }));

  // #49 — the PERFECT GAUNTLET badge is a top-right CORNER RIBBON, not a centered line above the score
  // (the old centered line at cy-30 crossed the 50px score at cy-6). A corner ribbon can never overlap it.
  if (data.perfectGauntlet) {
    const rx = cw / 2 - 8;
    const ry = cy - ch / 2 + 8;
    const rt = scene.add
      .text(0, 0, '★ PERFECT', { fontFamily: FONT.display, fontSize: `${scaledFont(11, 10)}px`, color: hex(PALETTE.ink), fontStyle: 'bold' })
      .setOrigin(1, 0);
    const rw = rt.width + 16;
    const rh = rt.height + 8;
    const rbg = scene.add.graphics();
    rbg.fillStyle(PALETTE.gold, 1);
    rbg.fillRoundedRect(rx - rw, ry, rw, rh, 8);
    rt.setPosition(rx - 8, ry + 4);
    card.add([rbg, rt]);
  }

  const scoreText = scene.add
    .text(0, cy - 6, score.toLocaleString(), {
      fontFamily: FONT.display,
      fontSize: `${scaledFont(50, 32)}px`,
      color: hex(PALETTE.player),
      fontStyle: 'bold',
    })
    .setOrigin(0.5)
    .setShadow(0, 0, hex(PALETTE.player), 18, false, true);
  card.add(scoreText);

  // tagline "I beat the rival. Can you beat me?"
  const tagY = cy + ch / 2 - 56;
  const tag = scene.add
    .text(0, tagY, 'I beat the rival. Can you beat ', {
      fontFamily: FONT.family,
      fontSize: `${scaledFont(14, 12)}px`,
      color: hex(PALETTE.text),
    })
    .setOrigin(0, 0.5);
  const me = scene.add
    .text(0, tagY, 'me?', { fontFamily: FONT.family, fontSize: `${scaledFont(14, 12)}px`, color: hex(PALETTE.gold), fontStyle: 'bold' })
    .setOrigin(0, 0.5);
  const totalW = tag.width + me.width;
  tag.setX(-totalW / 2);
  me.setX(-totalW / 2 + tag.width);
  card.add([tag, me]);

  // link chip
  const linkY = cy + ch / 2 - 24;
  const linkText = scene.add
    .text(0, linkY, url, { fontFamily: FONT.family, fontSize: `${scaledFont(12, 11)}px`, color: hex(PALETTE.scan) })
    .setOrigin(0, 0.5);
  // Leading link glyph inside the chip, left-grouped with the URL (matches the ref's chain icon).
  const linkIconS = 13;
  const linkGap = 7;
  const linkPad = 14;
  const lw = linkIconS + linkGap + linkText.width + linkPad * 2;
  const linkBg = scene.add.graphics();
  linkBg.fillStyle(PALETTE.surface2, 1);
  linkBg.fillRoundedRect(-lw / 2, linkY - 13, lw, 26, 13);
  linkBg.lineStyle(1, PALETTE.line, 1);
  linkBg.strokeRoundedRect(-lw / 2, linkY - 13, lw, 26, 13);
  const linkIcon = icon(scene, 'link', -lw / 2 + linkPad + linkIconS / 2, linkY, linkIconS, PALETTE.scan);
  linkText.setX(-lw / 2 + linkPad + linkIconS + linkGap);
  card.add(linkBg);
  card.add(linkIcon);
  card.add(linkText);

  // ── actions ──
  const shareY = cy + ch / 2 + 50;
  card.add(
    ctaButton(scene, 0, shareY, 'Share Result', () => (data.onShare ? data.onShare() : m.close()), {
      icon: 'share',
      width: cw,
      height: 56,
    }),
  );
  const rowY = shareY + 64;
  const halfW = (cw - 12) / 2;
  const copyBtn = secondaryButton(scene, -halfW / 2 - 6, rowY, 'Copy Link', () => data.onCopy?.(), { icon: 'copy', width: halfW, height: 46 });
  const saveBtn = secondaryButton(scene, halfW / 2 + 6, rowY, 'Save Image', () => data.onSave?.(), { icon: 'image', width: halfW, height: 46 });
  // ts150: same shrink-to-fit as the real-preview sheet — the mock is the dev-bridge inspection surface.
  fitButtonLabel(copyBtn, halfW);
  fitButtonLabel(saveBtn, halfW);
  card.add(copyBtn);
  card.add(saveBtn);

  return m;
}
